import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { FormattedFile } from "@/react/components/entityViews/files/context/EntityFileContext";
import FilePreview from "@/modern/components/file-preview";
import { isImageFileTyped } from "@/react/components/entityViews/files/components/FileHelpers";

interface FilePreviewAreaProps {
  file: FormattedFile;
  imageLoading: boolean;
  showSpinner: boolean;
  displayedImageUrl: string | undefined;
  currentImageUrl: string | undefined;
  hasPrevious: boolean;
  hasNext: boolean;
  onLoadComplete: () => void;
  onPrevious: () => void;
  onNext: () => void;
}

/**
 * File preview area with loading spinner and navigation arrows
 */
export const FilePreviewArea = ({
  file,
  imageLoading,
  showSpinner,
  displayedImageUrl,
  currentImageUrl,
  hasPrevious,
  hasNext,
  onLoadComplete,
  onPrevious,
  onNext
}: FilePreviewAreaProps) => {
  const isImage = isImageFileTyped(file);

  // For first load when displayedImageUrl is undefined, use currentImageUrl
  // After first load, always use displayedImageUrl to show previous image while new one loads
  const srcToDisplay = displayedImageUrl || (isImage ? currentImageUrl : undefined);

  // Only preload if we have a different URL and displayedImageUrl is set (not first load)
  const shouldPreload = isImage && currentImageUrl && displayedImageUrl && currentImageUrl !== displayedImageUrl;

  return (
    <div className="group relative flex min-h-[340px] flex-1 items-center justify-center overflow-hidden rounded-3xl border border-border/50">
      {/* Loading spinner overlay - shows after 300ms - only for images */}
      {isImage && showSpinner && imageLoading && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-card backdrop-blur-sm animate-in fade-in duration-200">
          <Loader2 className="h-12 w-12 animate-spin text-foreground" />
        </div>
      )}

      {/* Display file preview - shows the displayedImageUrl (previous image while loading new one) */}
      <FilePreview
        file={file}
        src={srcToDisplay}
        className="relative z-10 h-full w-full max-h-full rounded-2xl bg-card/50 object-contain shadow-2xl"
        fileIconClassName="size-24"
        showName={true}
        disableLoadingState={true}
      />

      {/* Preload new image invisibly - only when navigating (not first load) */}
      {shouldPreload && (
        <img
          src={currentImageUrl}
          alt=""
          className="hidden"
          onLoad={onLoadComplete}
          onError={onLoadComplete}
        />
      )}

      {/* Navigation arrows */}
      {hasPrevious && (
        <button
          type="button"
          onClick={onPrevious}
          className="absolute left-4 top-1/2 z-20 -translate-y-1/2 rounded-full bg-background/80 p-3 shadow-lg backdrop-blur-sm transition-opacity hover:bg-background opacity-0 group-hover:opacity-100 focus:opacity-100"
          aria-label="Previous file"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
      )}

      {hasNext && (
        <button
          type="button"
          onClick={onNext}
          className="absolute right-4 top-1/2 z-20 -translate-y-1/2 rounded-full bg-background/80 p-3 shadow-lg backdrop-blur-sm transition-opacity hover:bg-background opacity-0 group-hover:opacity-100 focus:opacity-100"
          aria-label="Next file"
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      )}
    </div>
  );
};
